import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  CssBaseline,
  Typography,
  Button,
  CircularProgress,
  Select,
  MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { MsalProvider, useMsal, MsalAuthenticationTemplate } from '@azure/msal-react';
import { PublicClientApplication, InteractionType, InteractionStatus } from '@azure/msal-browser';
import FdiRepositoryExplorer from './FdiRepositoryExplorer';
import FdiRepositoryUploader from './FdiRepositoryUploader';
import FdiRepositoryInformation from './FdiRepositoryInformation';
import FdiPackageSearch from './FdiPackageSearch';

// MSAL Configuration
const msalConfig = {
  auth: {
    clientId: 'b26c00a3-041b-4e10-8936-d10832000b3e',
    authority: 'https://login.microsoftonline.com/939a6f38-e5cb-4de2-b22f-a0d3a8213a2b/v2.0',
    redirectUri: process.env.REACT_APP_REDIRECT_URI || 'http://localhost:3000',
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
};

const msalInstance = new PublicClientApplication(msalConfig);

// Parse JWT payload
const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Error parsing JWT:', e);
    return {};
  }
};

// MSAL Initializer Component
function MsalInitializer({ children }) {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await msalInstance.initialize();
        await msalInstance.handleRedirectPromise();
        setIsInitialized(true);
      } catch (error) {
        console.error('MSAL initialization error:', error);
        setIsInitialized(true);
      }
    };
    initializeMsal();
  }, []);

  if (!isInitialized) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
}

// Login Page Component
function LoginPage({ roles, hasLoggedOut, userName }) {
  // Define role permissions mapping
  const rolePermissions = {
    'repository.reader': 'You can view repository data (read-only access).',
    'repository.contributor': 'You can view and upload data (read and write access, except for deleting).',
    'repository.manager': 'You can perform all actions, including viewing, uploading, and deleting data (full access).',
  };

  // If the user has just logged out, show a logged-out message
  if (hasLoggedOut) {
    return (
      <Box sx={{ textAlign: 'center', mt: 10 }}>
        <Typography variant="h4" gutterBottom>
          You have been logged out
        </Typography>
      </Box>
    );
  }

  // If the user is logged in, display welcome message and role permissions
  if (userName !== 'Guest') {
    // Parse roles into an array if it's a string (e.g., "repository.reader, repository.contributor")
    const userRoles = roles.includes(',') ? roles.split(', ').filter(role => role !== 'No roles') : [roles];
    const hasValidRoles = userRoles.length > 0 && userRoles[0] !== 'No roles';

    return (
      <Box sx={{ textAlign: 'center', mt: 10 }}>
        <Typography variant="h4" gutterBottom>
          Welcome to FDISync Tools, {userName}!
        </Typography>
        <Typography variant="h6" gutterBottom>
          Please select an app from the menu to continue.
        </Typography>
        {hasValidRoles && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Your Permissions:
            </Typography>
            {userRoles.map((role) => (
              <Typography key={role} variant="body1" sx={{ mb: 1 }}>
                <strong>{role}:</strong> {rolePermissions[role] || 'Unknown role permissions.'}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  }

  // User is not logged in, display welcome message
  return (
    <Box sx={{ textAlign: 'center', mt: 10 }}>
      <Typography variant="h4" gutterBottom>
        Welcome to FDISync Tools
      </Typography>
    </Box>
  );
}

// Protected Components with apiBaseUrl prop
function ProtectedExplorer({ apiBaseUrl, hasLoggedOut }) {
  if (hasLoggedOut) {
    return (
      <Box sx={{ textAlign: 'center', mt: 10 }}>
        <Typography variant="h4" gutterBottom>
          You have been logged out
        </Typography>
        <Typography variant="body1">
          Please log in again to access this page.
        </Typography>
      </Box>
    );
  }

  return (
    <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
      <FdiRepositoryExplorer apiBaseUrl={apiBaseUrl} />
    </MsalAuthenticationTemplate>
  );
}

function ProtectedUploader({ apiBaseUrl, hasLoggedOut }) {
  if (hasLoggedOut) {
    return (
      <Box sx={{ textAlign: 'center', mt: 10 }}>
        <Typography variant="h4" gutterBottom>
          You have been logged out
        </Typography>
        <Typography variant="body1">
          Please log in again to access this page.
        </Typography>
      </Box>
    );
  }

  return (
    <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
      <FdiRepositoryUploader apiBaseUrl={apiBaseUrl} />
    </MsalAuthenticationTemplate>
  );
}

function ProtectedInformation({ apiBaseUrl, hasLoggedOut }) {
  if (hasLoggedOut) {
    return (
      <Box sx={{ textAlign: 'center', mt: 10 }}>
        <Typography variant="h4" gutterBottom>
          You have been logged out
        </Typography>
        <Typography variant="body1">
          Please log in again to access this page.
        </Typography>
      </Box>
    );
  }

  return (
    <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
      <FdiRepositoryInformation apiBaseUrl={apiBaseUrl} />
    </MsalAuthenticationTemplate>
  );
}

function ProtectedPackageSearch({ apiBaseUrl, hasLoggedOut }) {
  if (hasLoggedOut) {
    return (
      <Box sx={{ textAlign: 'center', mt: 10 }}>
        <Typography variant="h4" gutterBottom>
          You have been logged out
        </Typography>
        <Typography variant="body1">
          Please log in again to access this page.
        </Typography>
      </Box>
    );
  }

  return (
    <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
      <FdiPackageSearch apiBaseUrl={apiBaseUrl} />
    </MsalAuthenticationTemplate>
  );
}

// Main App Component
function App() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { instance, accounts, inProgress } = useMsal();
  const [userName, setUserName] = useState('Guest');
  const [roles, setRoles] = useState('No roles');
  const [environment, setEnvironment] = useState('development');
  const [hasLoggedOut, setHasLoggedOut] = useState(false);
  const navigate = useNavigate();

  const environments = {
    production: 'https://fdirepoproxy.azurewebsites.net/repository/v1',
    development: 'https://fdirepoproxy-dev.azurewebsites.net/repository/v1',
    test: 'https://fdirepoproxy-test.azurewebsites.net/repository/v1' 
  };

  const apiBaseUrl = environments[environment] || process.env.REACT_APP_API_BASE_URL || 'https://fdirepoproxy.azurewebsites.net/repository/v1';

  useEffect(() => {
    const updateUserInfo = async () => {
      if (accounts.length > 0 && inProgress === InteractionStatus.None) {
        const idTokenClaims = accounts[0].idTokenClaims || {};
        setUserName(idTokenClaims.name || idTokenClaims.preferred_username || 'Guest');

        try {
          const response = await instance.acquireTokenSilent({
            scopes: ['api://3923e780-27fe-4174-a906-4659370dcd85/access_as_user'],
            account: accounts[0],
          });
          const tokenPayload = parseJwt(response.accessToken);
          const roleList = tokenPayload.roles || [];
          setRoles(roleList.length > 0 ? roleList.join(', ') : 'No roles');
        } catch (error) {
          console.error('Error acquiring access token:', error);
          setRoles(`No roles (error: ${error.message})`);
        }
      } else {
        setUserName('Guest');
        setRoles('No roles');
      }
    };
    updateUserInfo();
  }, [instance, accounts, inProgress]);

  const handleEnvironmentChange = (event) => {
    setEnvironment(event.target.value);
    console.log('Environment switched to:', event.target.value);
  };

  const handleLogin = async () => {
    try {
      await instance.loginRedirect({
        scopes: ['api://3923e780-27fe-4174-a906-4659370dcd85/access_as_user'],
      });
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  const handleLogout = () => {
    console.log('Logout triggered');
    // Clear MSAL cache and session state
    instance.setActiveAccount(null);
    instance.clearCache();
    // Use logoutRedirect with onRedirectNavigate to prevent redirect to AAD logout page
    instance.logoutRedirect({
      endSessionEndpoint: false, // Ensures we don't end the AAD SSO session
      onRedirectNavigate: (url) => {
        // Prevent the redirect to the AAD logout page
        return false;
      },
    });
    setHasLoggedOut(true); // Set flag to indicate logout
    navigate('/'); // Manually redirect to the app's root URL
  };

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const menuItems = [
    { text: 'Information', path: '/information' },
    { text: 'Explorer', path: '/explorer' },
    { text: 'Uploader', path: '/uploader' },
    { text: 'Package Search', path: '/search' },
  ];

  const drawerContent = (
    <Box sx={{ width: 250 }} role="presentation" onClick={toggleDrawer(false)} onKeyDown={toggleDrawer(false)}>
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} component={Link} to={item.path} button={true}>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <div>
      <CssBaseline />
      <AppBar position="static">
        <Toolbar>
          <IconButton edge="start" color="inherit" aria-label="menu" onClick={toggleDrawer(true)}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" style={{ flexGrow: 1 }}>
            FDISync Tools
          </Typography>
          <Typography variant="body1" style={{ marginRight: '20px' }}>
            User: {userName}
          </Typography>
          <Typography variant="body1" style={{ marginRight: '20px' }}>
            Role: {roles}
          </Typography>
          <Select
            value={environment}
            onChange={handleEnvironmentChange}
            sx={{ color: 'white', mr: 2 }}
            size="small"
          >
            <MenuItem value="production">Production</MenuItem>
            <MenuItem value="test">Test</MenuItem>
            <MenuItem value="development">Development</MenuItem>
          </Select>
          {accounts.length > 0 ? (
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          ) : (
            <Button color="inherit" onClick={handleLogin}>
              Login
            </Button>
          )}
        </Toolbar>
      </AppBar>
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        {drawerContent}
      </Drawer>
      <Routes>
        <Route path="/" element={<LoginPage roles={roles} hasLoggedOut={hasLoggedOut} userName={userName} />} />
        <Route path="/explorer" element={<ProtectedExplorer apiBaseUrl={apiBaseUrl} hasLoggedOut={hasLoggedOut} />} />
        <Route path="/uploader" element={<ProtectedUploader apiBaseUrl={apiBaseUrl} hasLoggedOut={hasLoggedOut} />} />
        <Route path="/information" element={<ProtectedInformation apiBaseUrl={apiBaseUrl} hasLoggedOut={hasLoggedOut} />} />
        <Route path="/search" element={<ProtectedPackageSearch apiBaseUrl={apiBaseUrl} hasLoggedOut={hasLoggedOut} />} />
      </Routes>
    </div>
  );
}

// Root Component with MSAL Initialization and Router
function Root() {
  return (
    <MsalInitializer>
      <Router>
        <App />
      </Router>
    </MsalInitializer>
  );
}

export default Root;